import React, { createContext, useState, useContext } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';

const DataContext = createContext(null);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [samples, setSamples] = useState([]);
  const [csvFiles, setCsvFiles] = useState([]);
  const [sampleCounts, setSampleCounts] = useState({
    total_samples: 0,
    parked: 0,
    labeled: 0,
    cortex: 0,
  });
  const [loading, setLoading] = useState(false);

  const uploadCSV = async (projectId, sensorId, file) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('csv_files', file);

      const response = await api.post(`/sensor/${sensorId}/save-csv/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('File uploaded successfully');
      return response.data;
    } catch (error) {
      toast.error('Failed to upload file');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const plotCSV = async (sensorId, files, samplesToRemove = []) => {
    try {
      setLoading(true);
      const formData = new FormData();

      // Support multiple files
      if (Array.isArray(files)) {
        files.forEach(file => formData.append('csv_files', file));
      } else {
        formData.append('csv_files', files);
      }

      // Add samples to exclude from QT/Mahalanobis recalculation
      if (samplesToRemove && samplesToRemove.length > 0) {
        samplesToRemove.forEach(name => formData.append('samples_to_remove', name));
      }

      const response = await api.post(`/sensor/${sensorId}/plot-csv/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data; // Returns { chart_html, chart_qt_html, chart_mahanalobis_html }
    } catch (error) {
      toast.error('Failed to generate plots');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const saveToPark = async (sensorId, files, samplesToRemove = []) => {
    try {
      setLoading(true);
      const formData = new FormData();

      // Support multiple files
      if (Array.isArray(files)) {
        files.forEach(file => formData.append('csv_files', file));
      } else {
        formData.append('csv_files', files);
      }

      // Add samples to remove
      if (samplesToRemove.length > 0) {
        formData.append('samples_to_remove', JSON.stringify(samplesToRemove));
      }

      const response = await api.post(`/sensor/${sensorId}/save-csv/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(`Saved ${files.length} file(s) to Park successfully`);
      return response.data;
    } catch (error) {
      toast.error('Failed to save to Park');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchSamples = async (projectId, filters = {}) => {
    try {
      setLoading(true);

      // Helper function to fetch all pages
      const fetchAllPages = async (endpoint, params = {}) => {
        let allData = [];
        let nextUrl = endpoint;
        let page = 1;

        while (nextUrl) {
          const response = await api.get(nextUrl, { params: page === 1 ? params : {} });
          const samplesData = response.data.results || response.data || [];

          if (Array.isArray(samplesData)) {
            allData.push(...samplesData);
          }

          // Check if there's a next page
          nextUrl = response.data.next;

          // If next is a full URL, extract the path
          if (nextUrl && nextUrl.startsWith('http')) {
            const url = new URL(nextUrl);
            nextUrl = url.pathname + url.search;
          }

          page++;
        }

        return allData;
      };

      // If status is specified, fetch only that status with all pages
      if (filters.status) {
        const endpoint = `/project/${projectId}/samples/${filters.status}/`;
        const samplesData = await fetchAllPages(endpoint, { ...filters, status: undefined });
        setSamples(samplesData);
        return samplesData;
      }

      // Otherwise, fetch from all statuses and combine
      const statuses = ['Labeled', 'Parked', 'Cortex', 'Legacy'];
      const allSamples = [];

      for (const status of statuses) {
        try {
          const endpoint = `/project/${projectId}/samples/${status}/`;
          const samplesData = await fetchAllPages(endpoint, filters);
          allSamples.push(...samplesData);
        } catch (err) {
          // Continue if a specific status fails
          console.warn(`Failed to fetch ${status} samples:`, err);
        }
      }

      setSamples(allSamples);
      return allSamples;
    } catch (error) {
      console.error('Error fetching samples:', error);
      toast.error('Failed to fetch samples');
      setSamples([]);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateSample = async (sampleId, data) => {
    try {
      const response = await api.patch(`/sample/${sampleId}/`, data);
      toast.success('Sample updated successfully');
      return response.data;
    } catch (error) {
      toast.error('Failed to update sample');
      throw error;
    }
  };

  const deleteSample = async (sampleId) => {
    try {
      await api.delete(`/sample/${sampleId}/`);
      toast.success('Sample deleted successfully');
    } catch (error) {
      toast.error('Failed to delete sample');
      throw error;
    }
  };

  // Sample status management
  const updateSampleStatus = async (sampleId, status) => {
    try {
      const response = await api.patch(`/sample/${sampleId}/status/`, { status });
      toast.success(`Sample moved to ${status}`);
      return response.data;
    } catch (error) {
      toast.error('Failed to update sample status');
      throw error;
    }
  };

  const bulkUpdateStatus = async (sampleIds, status) => {
    try {
      setLoading(true);
      await Promise.all(
        sampleIds.map((sampleId) =>
          api.patch(`/sample/${sampleId}/status/`, { status })
        )
      );
      toast.success(`${sampleIds.length} samples moved to ${status}`);
    } catch (error) {
      toast.error('Failed to update sample statuses');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Sample annotation/tagging
  const annotateSample = async (sampleId, sampleName, tags) => {
    try {
      const response = await api.post(`/sample/${sampleId}/tag-editor/`, {
        sample_name: sampleName,
        tags: tags,
      });
      toast.success('Sample annotated successfully');
      return response.data;
    } catch (error) {
      toast.error('Failed to annotate sample');
      throw error;
    }
  };

  // Sample statistics
  const fetchSampleCounts = async (projectId) => {
    try {
      const response = await api.get(`/project/${projectId}/samples-count/`);
      setSampleCounts(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching sample counts:', error);
      toast.error('Failed to fetch sample statistics');
      throw error;
    }
  };

  // Fetch samples with tags/labels
  const fetchTaggedSamples = async (projectId, filters = {}) => {
    try {
      setLoading(true);

      // Helper function to fetch all pages
      const fetchAllPages = async (endpoint, params = {}) => {
        let allData = [];
        let nextUrl = endpoint;
        let page = 1;

        while (nextUrl) {
          const response = await api.get(nextUrl, { params: page === 1 ? params : {} });
          const samplesData = response.data.results || response.data || [];

          if (Array.isArray(samplesData)) {
            allData.push(...samplesData);
          }

          // Check if there's a next page
          nextUrl = response.data.next;

          // If next is a full URL, extract the path
          if (nextUrl && nextUrl.startsWith('http')) {
            const url = new URL(nextUrl);
            nextUrl = url.pathname + url.search;
          }

          page++;
        }

        return allData;
      };

      // If status is specified, fetch only that status with all pages
      if (filters.status) {
        const endpoint = `/project/${projectId}/samples-tags/status/${filters.status}/`;
        const samplesData = await fetchAllPages(endpoint, filters.page ? { page: filters.page } : {});
        setSamples(samplesData);
        return samplesData;
      }

      // If label filter is specified
      if (filters.labelId) {
        let endpoint = `/project/${projectId}/samples-tags/label/${filters.labelId}/`;
        if (filters.choiceId) {
          endpoint += `choice/${filters.choiceId}/`;
        }
        const samplesData = await fetchAllPages(endpoint, filters.page ? { page: filters.page } : {});
        setSamples(samplesData);
        return samplesData;
      }

      // Otherwise, fetch from all statuses and combine
      const statuses = ['Labeled', 'Parked', 'Cortex', 'Legacy'];
      const allSamples = [];

      for (const status of statuses) {
        try {
          const endpoint = `/project/${projectId}/samples-tags/status/${status}/`;
          const samplesData = await fetchAllPages(endpoint);
          allSamples.push(...samplesData);
        } catch (err) {
          console.warn(`Failed to fetch ${status} tagged samples:`, err);
        }
      }

      setSamples(allSamples);
      return allSamples;
    } catch (error) {
      console.error('Error fetching tagged samples:', error);
      toast.error('Failed to fetch samples');
      setSamples([]);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    samples,
    setSamples,
    csvFiles,
    setCsvFiles,
    sampleCounts,
    setSampleCounts,
    loading,
    uploadCSV,
    plotCSV,
    saveToPark,
    fetchSamples,
    fetchTaggedSamples,
    fetchSampleCounts,
    updateSample,
    deleteSample,
    updateSampleStatus,
    bulkUpdateStatus,
    annotateSample,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
